import React from "react";
import { Button, Select, Tooltip } from "antd";
import { 
  FullscreenOutlined, 
  FullscreenExitOutlined, 
  ZoomInOutlined, 
  ZoomOutOutlined, 
  CodeOutlined,
  EyeOutlined,
} from "@ant-design/icons";

interface PreviewToolbarProps {
  fileType: string;
  htmlViewMode?: "code" | "rendered";
  isFullscreen: boolean;
  zoom: number;
  selectedTheme: string;
  syntaxThemes: Array<{ label: string; value: string; style: any }>;
  disableThemeSelector: boolean;
  onHtmlViewModeChange?: (mode: "code" | "rendered") => void;
  onFullscreenToggle: () => void;
  onZoomChange: (zoom: number) => void;
  onThemeChange: (theme: string) => void;
  onErrorReset?: () => void;
}

const PreviewToolbar: React.FC<PreviewToolbarProps> = ({
  fileType,
  htmlViewMode = "code",
  isFullscreen,
  zoom,
  selectedTheme,
  syntaxThemes,
  disableThemeSelector,
  onHtmlViewModeChange,
  onFullscreenToggle,
  onZoomChange,
  onThemeChange,
  onErrorReset,
}) => {
  const renderFullscreenButton = () => (
    <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} placement={"rightTop"}>
      <Button
        icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        onClick={onFullscreenToggle}
        size="small"
      />
    </Tooltip>
  );

  const renderZoomControls = () => (
    <>
      <Tooltip title="Zoom Out" placement={"rightTop"}>
        <Button
          icon={<ZoomOutOutlined />}
          onClick={() => onZoomChange(Math.max(0.5, zoom - 0.1))}
          size="small"
        />
      </Tooltip>
      <Tooltip title="Zoom In" placement={"rightTop"}>
        <Button
          icon={<ZoomInOutlined />}
          onClick={() => onZoomChange(Math.min(3, zoom + 0.1))}
          size="small"
        />
      </Tooltip>
      <Tooltip title="Reset Zoom" placement={"rightTop"}>
        <Button
          icon={<span style={{ fontWeight: 700, fontSize: 16 }}>1x</span>}
          onClick={() => onZoomChange(1.0)}
          size="small"
        />
      </Tooltip>
    </>
  );

  const renderThemeSelector = () => (
    <Select
      size="small"
      value={selectedTheme}
      onChange={onThemeChange}
      style={{ width: 160, marginLeft: 8 }}
      options={syntaxThemes.map(({ label, value }) => ({ label, value }))}
    />
  );

  const renderHtmlViewModeToggle = () => (
    <Tooltip 
      title={htmlViewMode === "code" ? "Switch to Rendered View" : "Switch to Code View"} 
      placement={"rightTop"}
    >
      <Button
        icon={htmlViewMode === "code" ? <EyeOutlined /> : <CodeOutlined />}
        onClick={() => {
          if (onHtmlViewModeChange) {
            onHtmlViewModeChange(htmlViewMode === "code" ? "rendered" : "code");
            onErrorReset?.();
          }
        }}
        size="small"
      >
        {htmlViewMode === "code" ? "Preview" : "Code"}
      </Button>
    </Tooltip>
  );

  const controls = [];

  // HTML view mode toggle for HTML files only
  if (fileType === "html") {
    controls.push(renderHtmlViewModeToggle());
  }

  // Theme selector for code files only (or HTML in code mode)
  if ((fileType === "code" || (fileType === "html" && htmlViewMode === "code")) && disableThemeSelector) {
    controls.push(renderThemeSelector());
  }

  // Fullscreen for all supported file types
  if (["image", "code", "html", "pdf", "csv", "json"].includes(fileType)) {
    controls.push(renderFullscreenButton());
  }

  // Zoom controls for image, code, html (code mode), and pdf only
  if (fileType === "image" || fileType === "code" || fileType === "pdf" || (fileType === "html" && htmlViewMode === "code")) {
    controls.push(renderZoomControls());
  }

  return controls.length > 0 ? (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 16 }}>
      {controls.map((control, index) => (
        <React.Fragment key={index}>{control}</React.Fragment>
      ))}
    </div>
  ) : null;
};

export default PreviewToolbar;
